/**
 * Progress of fixes a chat started, read from the project's task list.
 *
 * The chat told subscribers "I'll post each pull request here as it opens" and
 * then never did (canary 2026-10-03). This turns a fresh task list into the
 * message to post and the fixes that no longer need watching.
 */
export type FixProgressTask = {
  id: string;
  title?: string;
  priority?: string;
  file_path?: string | null;
  phase_id?: string | null;
  status: string;
  suppression_reason?: string | null;
  remediation_pr_number?: number | null;
  /** Latest note the engine left on the task (why a pull request is waiting, or that it is merging). */
  last_event_message?: string | null;
  fix_queue?: { state: 'queued' | 'running'; reason: string | null } | null;
};

/** A live queue request: the task can still read open/failed until a worker fences it. */
export function hasActiveFixQueue(task: Pick<FixProgressTask, 'fix_queue'>): boolean {
  return task.fix_queue?.state === 'queued' || task.fix_queue?.state === 'running';
}

/** Queue errors may contain provider details or secrets. Only emit fixed user copy. */
function queuedFixDetail(reason: string | null): string {
  const value = reason?.trim();
  if (!value) return 'Fix queued. Waiting for a worker\u2026';
  if (value === 'Waiting for another project pull request to be merged or reconciled') {
    return 'Fix delayed while another pull request in this project awaits review or verification.';
  }
  if (value === 'Reconciliation deferred: Previous pipeline may still be active') {
    return 'Fix delayed while previous work is still running.';
  }
  if (value.startsWith('Reconciliation deferred:')) {
    return 'Fix delayed while we verify previous work. Progress will update when verification completes.';
  }
  return 'Fix delayed. Progress will update when it can continue.';
}

/** Note the engine leaves while it is still merging a pull request it opened. */
export const MERGING_NOTE_PREFIX = 'Merging pull request';
/** Notes that explain why an open pull request is waiting on the customer. */
const WAITING_NOTE_PREFIXES = ['Waiting for your approval', 'Could not merge'];

export interface FixActivityRow {
  id: string;
  title: string;
  state: 'running' | 'merging' | 'waiting' | 'paused' | 'fixed' | 'failed';
  pr: number | null;
  detail: string;
}

/** Recover the latest known fix outcomes from durable tasks, including after a
 * reload. This is a current status view, not a reconstructed chat transcript. */
export function fixActivityRows(tasks: FixProgressTask[]): FixActivityRow[] {
  const seen = new Set<string>();
  const rows: FixActivityRow[] = [];
  const keyOf = (task: FixProgressTask): string => `${task.priority ?? ''}|${(task.title ?? '').trim().toLowerCase()}|${task.file_path ?? ''}|${task.phase_id ?? ''}`;
  for (const latest of tasks) {
    // Superseded observations carry no current work. The referenced canonical
    // task must remain visible even when a later Sync recorded this copy.
    if (latest.status === 'suppressed' && latest.suppression_reason?.startsWith('superseded_by:')) continue;
    const key = keyOf(latest);
    // Tasks arrive newest first. The latest finding supersedes historical
    // copies, including an open finding with an older fixed copy.
    if (seen.has(key)) continue;
    seen.add(key);
    // Legacy syncs may have copied a finding while an older copy's fix still
    // runs. Keep that real running/open-PR or queued work visible until reconciliation.
    const task = latest.status === 'open'
      ? tasks.find((candidate: FixProgressTask) => keyOf(candidate) === key && candidate.status === 'in_progress')
        ?? tasks.find((candidate: FixProgressTask) => keyOf(candidate) === key
          && (candidate.status === 'open' || candidate.status === 'failed') && hasActiveFixQueue(candidate))
        ?? latest
      : latest;
    const pr = typeof task.remediation_pr_number === 'number' && task.remediation_pr_number > 0 ? task.remediation_pr_number : null;
    const note = (task.last_event_message ?? '').trim();
    if (task.status === 'fixed') rows.push({ id: task.id, title: label(task), state: 'fixed', pr, detail: 'Fixed' });
    else if (task.status === 'failed' && !hasActiveFixQueue(task)) rows.push({ id: task.id, title: label(task), state: 'failed', pr,
      detail: pr ? `Fix did not finish. Pull request #${pr} exists; inspect the failure before merging.` : 'Fix did not finish. Select the issue below to retry.' });
    else if (pr && note.startsWith(MERGING_NOTE_PREFIX)) rows.push({ id: task.id, title: label(task), state: 'merging', pr, detail: 'Merging pull request…' });
    else if (pr) rows.push({ id: task.id, title: label(task), state: 'waiting', pr,
      detail: WAITING_NOTE_PREFIXES.some((prefix: string) => note.startsWith(prefix)) ? note : 'Pull request open for review.' });
    else if (task.status === 'in_progress' && note.startsWith('Reconciliation paused:') && task.fix_queue === null)
      rows.push({ id: task.id, title: label(task), state: 'paused', pr, detail: 'Reconciliation paused. Retry to check the existing fix run and recover its progress.' });
    else if (task.status === 'in_progress' || hasActiveFixQueue(task)) rows.push({ id: task.id, title: label(task), state: 'running', pr,
      detail: task.fix_queue?.state === 'queued' ? queuedFixDetail(task.fix_queue.reason) : 'Fix queued or running…' });
  }
  return rows;
}

export interface FixProgressUpdate {
  /** One chat message covering everything that changed, or null when nothing did. */
  message: string | null;
  /** Fixes that reached an outcome (pull request open, fixed, or failed). */
  settledIds: string[];
  /** How many of those ended with a pull request open, waiting for review. */
  opened: number;
  /** How many of those ended merged (the issue is fixed). */
  merged: number;
  /** How many of those did not finish. */
  failed: number;
  /** A confirmed paused reconciliation is an observation, never failure or success. */
  paused?: number;
}

/** Running totals for the fixes a chat is watching. */
export interface FixWatchCounts {
  total: number;
  remaining: number;
  opened: number;
  merged: number;
  failed: number;
  paused?: number;
}

/** https://github.com/<owner>/<name>, or null when the repo is not known. */
export function repoWebUrl(repo: { owner?: string | null; name?: string | null } | null | undefined): string | null {
  const owner = repo?.owner?.trim();
  const name = repo?.name?.trim();
  return owner && name ? `https://github.com/${owner}/${name}` : null;
}

function label(task: FixProgressTask): string {
  return (task.title ?? '').trim() || 'this issue';
}

export function fixProgressUpdates(
  tasks: FixProgressTask[],
  pendingIds: Iterable<string>,
  repoUrl: string | null,
): FixProgressUpdate {
  const byId = new Map(tasks.map((task) => [task.id, task] as const));
  const lines: string[] = [];
  const settledIds: string[] = [];
  let opened = 0;
  let merged = 0;
  let failed = 0;
  let paused = 0;
  for (const id of pendingIds) {
    const task = byId.get(id);
    if (!task) continue;
    const pr = task.remediation_pr_number;
    const ref = typeof pr === 'number' && pr > 0
      ? (repoUrl ? `[#${pr}](${repoUrl}/pull/${pr})` : `#${pr}`)
      : null;
    if (task.status === 'fixed') {
      lines.push(ref ? `Pull request ${ref} is merged: "${label(task)}" is fixed.` : `"${label(task)}" is fixed.`);
      settledIds.push(id);
      merged += 1;
    } else if (task.status === 'failed' && !hasActiveFixQueue(task)) {
      lines.push(ref ? `The fix for "${label(task)}" did not finish. Pull request ${ref} exists; inspect the failure before merging.`
        : `The fix for "${label(task)}" did not finish. It is back in the checklist so you can retry it.`);
      settledIds.push(id);
      failed += 1;
    } else if (ref) {
      const note = (task.last_event_message ?? '').trim();
      // The engine is still merging this one: keep watching, say nothing yet.
      if (note.startsWith(MERGING_NOTE_PREFIX)) continue;
      const why = WAITING_NOTE_PREFIXES.some((prefix) => note.startsWith(prefix)) ? note : 'Review and merge it on GitHub.';
      lines.push(`Pull request ${ref} is open for "${label(task)}". ${why}`);
      settledIds.push(id);
      opened += 1;

    } else if (task.status === 'in_progress' && task.fix_queue === null
      && (task.last_event_message ?? '').trim().startsWith('Reconciliation paused:')) {
      lines.push(`Reconciliation for "${label(task)}" is paused. Use Retry reconciliation in Fix progress to check the existing run.`);
      settledIds.push(id);
      paused += 1;
    }
  }
  if (lines.length === 0) return { message: null, settledIds, opened, merged, failed };
  return { message: lines.length === 1 ? lines[0]! : lines.map((line) => `- ${line}`).join('\n'), settledIds, opened, merged, failed,
    ...(paused > 0 ? { paused } : {}) };
}

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

/** The live line shown beside the spinner while fixes are in flight. */
export function fixProgressLabel(counts: FixWatchCounts): string {
  if (counts.remaining >= counts.total) return `Working on ${plural(counts.total, 'fix', 'fixes')}…`;
  const parts = [`${counts.remaining} of ${plural(counts.total, 'fix', 'fixes')} still running`];
  if (counts.merged > 0) parts.push(`${counts.merged} merged`);
  if (counts.opened > 0) parts.push(`${plural(counts.opened, 'pull request', 'pull requests')} waiting for your review`);
  if (counts.failed > 0) parts.push(`${counts.failed} did not finish`);
  if (counts.paused) parts.push(`${counts.paused} reconciliation paused`);
  return parts.join(' · ');
}

/** Closing message once every watched fix has an outcome (null for a single fix: its own message says it all). */
export function fixRunSummary(counts: FixWatchCounts): string | null {
  if (counts.total <= 1) return null;
  const parts: string[] = [];
  if (counts.merged > 0) parts.push(`${counts.merged} merged`);
  if (counts.opened > 0) parts.push(`${plural(counts.opened, 'pull request is', 'pull requests are')} waiting for your review`);
  if (counts.failed > 0) parts.push(`${plural(counts.failed, 'fix', 'fixes')} did not finish and ${counts.failed === 1 ? 'is' : 'are'} back in the checklist`);
  if (counts.paused) parts.push(`${plural(counts.paused, 'fix', 'fixes')} paused; retry reconciliation in Fix progress`);
  return parts.length > 0 ? `${counts.paused ? 'Progress for' : 'Finished all'} ${counts.total} fixes: ${parts.join('; ')}.` : null;
}

/** What to say right after a batch of fix requests was sent. */
export function fixStartMessage(result: { requested: number; started: number; alreadyRunning: number; failed: string[] }): string {
  const follow = "I'll post each pull request here as it opens.";
  if (result.started === result.requested) {
    return `${result.started === 1 ? 'The fix is' : `All ${result.started} fixes are`} queued. ${follow}`;
  }
  const parts = [`${result.started} of ${plural(result.requested, 'fix', 'fixes')} queued.`];
  if (result.alreadyRunning > 0) {
    parts.push(`${result.alreadyRunning} already ${result.alreadyRunning === 1 ? 'has' : 'have'} a fix in progress or a pull request open.`);
  }
  if (result.failed.length > 0) parts.push(`${result.failed.length} could not start: ${result.failed[0]}`);
  if (result.started > 0) parts.push(follow);
  return parts.join(' ');
}

/** Does this message ask to merge the open fix pull requests? */
export { hasMergeIntent } from '@ocnexus/contracts';

export interface FixMergeResult {
  title: string;
  pr_number: number;
  merged: boolean;
  /** Why it was not merged. */
  note?: string;
}

/** What to say after the customer asked for the open fix pull requests to be merged. */
export function mergeResultsMessage(results: FixMergeResult[], repoUrl: string | null): string {
  if (results.length === 0) return 'There are no open fix pull requests to merge.';
  const ref = (pr: number): string => (repoUrl ? `[#${pr}](${repoUrl}/pull/${pr})` : `#${pr}`);
  const lines = results.map((result) => (result.merged
    ? `Pull request ${ref(result.pr_number)} is merged: "${result.title}" is fixed.`
    : `Pull request ${ref(result.pr_number)} is still open for "${result.title}". ${result.note ?? 'It could not be merged.'}`));
  if (lines.length === 1) return lines[0]!;
  const merged = results.filter((result) => result.merged).length;
  return `Merged ${merged} of ${plural(results.length, 'pull request', 'pull requests')}.\n${lines.map((line) => `- ${line}`).join('\n')}`;
}
